import { Injectable } from '@angular/core';
import { SectionsService } from '@proxy/configurations';
import { SectionsDto } from '@proxy/configurations/sections';
import { Observable, ReplaySubject, of, firstValueFrom, map } from 'rxjs';

/**
 * Section Cache Service - Permanent caching for section data
 * 
 * Wraps the ABP proxy SectionsService with caching.
 * Sections rarely change so they are cached permanently.
 * 
 * Features:
 * - Loads all sections once on first access
 * - Permanent cache (sections don't change frequently)
 * - Quick lookup by ID, slug, or name
 * - Manual refresh capability if needed
 */
@Injectable({
  providedIn: 'root',
})
export class SectionService {
  // Use ReplaySubject(1) to cache the last emitted value for late subscribers
  private sectionsSubject = new ReplaySubject<SectionsDto[]>(1);
  private cachedSections: SectionsDto[] = [];
  private isLoaded = false;
  private isLoading = false;
  
  // Quick lookup maps
  private byId: Map<string, SectionsDto> = new Map();
  private bySlug: Map<string, SectionsDto> = new Map();
  private byName: Map<string, SectionsDto> = new Map();

  // Map of section slugs to section names
  private readonly sectionSlugMap = {
    'project-management': { name: 'Project Management' },
    'process-design': { name: 'Process Design' },
    'technical-design': { name: 'Technical Design' },
    'testing': { name: 'Testing' },
    'reports': { name: 'Reports' },
  };

  constructor(private sectionsProxy: SectionsService) {}

  /**
   * Get all sections with permanent caching (Observable)
   * Uses ReplaySubject to ensure late subscribers get cached data
   */
  getAllSections(): Observable<SectionsDto[]> {
    // If already loaded, return immediately from cache
    if (this.isLoaded && this.cachedSections.length > 0) {
      return of(this.cachedSections);
    }
    
    // If not loading yet, start loading
    if (!this.isLoading) {
      this.isLoading = true;
      
      this.sectionsProxy
        .getList({ maxResultCount: 100, skipCount: 0, sorting: '' })
        .subscribe({
          next: (response) => {
            this.cachedSections = response.items || [];
            this.rebuildIndexes();
            this.isLoaded = true;
            this.isLoading = false;
            this.sectionsSubject.next(this.cachedSections);
          },
          error: (err) => {
            console.error('Error loading sections:', err);
            this.isLoading = false;
            this.sectionsSubject.next([]);
          }
        });
    }
    
    // Return the subject - ReplaySubject will emit the last value to late subscribers
    return this.sectionsSubject.asObservable();
  }

  /**
   * Initialize cache (call on app startup for best performance)
   */
  async init(): Promise<void> {
    if (this.isLoaded) return;
    
    // If already loading, wait for it
    if (this.isLoading) {
      await firstValueFrom(this.sectionsSubject);
      return;
    }
    
    this.isLoading = true;
    
    try {
      const response = await firstValueFrom(
        this.sectionsProxy.getList({ maxResultCount: 100, skipCount: 0, sorting: '' })
      );
      this.cachedSections = response.items || [];
      this.rebuildIndexes();
      this.isLoaded = true;
      this.sectionsSubject.next(this.cachedSections);
    } catch (err) {
      console.error('Error loading sections:', err);
      this.sectionsSubject.next([]);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get cached sections synchronously (empty if not loaded yet)
   */
  getCachedSections(): SectionsDto[] {
    return this.cachedSections;
  }

  /**
   * Get section by ID (sync lookup from cache)
   */
  getSectionById(id: string): SectionsDto | undefined {
    return this.byId.get(id);
  }

  /**
   * Get section by name (sync lookup from cache)
   */
  getSectionByName(name: string): SectionsDto | undefined {
    return this.byName.get(name);
  }

  /**
   * Get section by slug (URL-friendly name) - Observable
   */
  getSectionBySlug(slug: string): Observable<SectionDto> {
    const sectionInfo = this.getSectionInfoBySlug(slug);
    if (!sectionInfo) {
      return of(null);
    }
    return this.getAllSections().pipe(
      map(sections => sections.find(s => s.name === sectionInfo.name))
    );
  }

  /**
   * Get section by slug synchronously (from cache)
   */
  getSectionBySlugSync(slug: string): SectionsDto | undefined {
    return this.bySlug.get(slug);
  }

  /**
   * Get section info from slug (static mapping)
   */
  getSectionInfoBySlug(slug: string): { name: string } {
    return this.sectionSlugMap[slug];
  }

  /**
   * Convert section name to slug
   */
  getSlugFromName(name: string): string {
    if (!name) return 'section';
    
    for (const [slug, info] of Object.entries(this.sectionSlugMap)) {
      if (info.name === name) return slug;
    }
    
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Rebuild lookup indexes after cache load
   */
  private rebuildIndexes(): void {
    this.byId.clear();
    this.bySlug.clear();
    this.byName.clear();
    
    for (const section of this.cachedSections) {
      if (section.id) {
        this.byId.set(section.id, section);
      }
      if (section.name) {
        this.byName.set(section.name, section);
        const slug = this.getSlugFromName(section.name);
        this.bySlug.set(slug, section);
      }
    }
  }

  /**
   * Force refresh cache (rarely needed for sections)
   */
  refresh(): void {
    this.cachedSections = [];
    this.isLoaded = false;
    this.isLoading = false;
    // Create new ReplaySubject to clear old cached value
    this.sectionsSubject = new ReplaySubject<SectionsDto[]>(1);
    this.byId.clear();
    this.bySlug.clear();
    this.byName.clear();
  }
}

// Strongly typed section data interface
export interface SectionDto extends SectionsDto {
  id?: string;
  name?: string;
}